import { useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import SyncCard from 'components/UI/SyncCard';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import ContainedButton from 'components/Buttons/ContainedButton';
import AcceptOfferDialog from 'parts/Assets/Dialogs/AcceptOfferDialog';
import ArrowIcon from 'components/Icons/ArrowLeftIcon';
import PAGES from 'constants/links/pages';
import { getDays, calculateRate, compareByValue } from 'utils/utility';
import { STATUS } from 'parts/Assets/Dialogs/AcceptInfo';
import { acceptOffer } from 'utils/web3';
import { deleteOffer } from 'services/nftfi';
import { cryptoMap } from 'config/contracts';
import { OrderType } from 'constants/types';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(1),
    overflowX: 'auto',
    width: '100%',
  },
  table: {
    width: '100%',
    textAlign: 'center',
    overflow: 'auto',
    '& thead th p': {
      whiteSpace: 'nowrap',
    },
    '& p': {
      padding: theme.spacing(0, 2),
    },
    '& th': {
      cursor: 'pointer',
    },
    '& th:hover': {
      opacity: 0.75,
    },
  },
  tableRow: {
    cursor: 'pointer',
    boxShadow: 'inset -5px 2px 32px -9px #c5c5c5',
    animation: 'pulse 2s infinite',
  },
  active: {
    boxShadow: 'inset -5px 2px 32px -9px #06bcff',
  },
  link: {
    textDecoration: 'underline',
  },
  assetName: {
    // maxWidth: 240,
  },
  arrowUp: {
    transform: 'rotate(-90deg)',
    width: 16,
    height: 16,
  },
  arrowDown: {
    transform: 'rotate(90deg)',
    width: 16,
    height: 16,
  },
}));

function OffersTable({
  address,
  offers,
  setOffers,
  loading,
  isOwner,
  hideAssetName,
  title = 'OFFERS RECEIVED',
  fetchAsset,
  assetStatus,
}) {
  const classes = useStyles();
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [status, setStatus] = useState(STATUS.ACCEPT_BEFORE);

  const [sortBy, setSortBy] = useState('');
  const [orderBy, setOrderBy] = useState(OrderType.DEFAULT);

  const sortHandler = key => () => {
    if (sortBy === key) {
      setOrderBy(
        orderBy === OrderType.DEFAULT
          ? OrderType.ASCENDING
          : orderBy === OrderType.ASCENDING
          ? OrderType.DESCENDING
          : OrderType.DEFAULT,
      );
    } else {
      setSortBy(key);
      setOrderBy(OrderType.ASCENDING);
    }
  };

  const TH = ({ sortKey, label, align }) => (
    <th onClick={sortKey ? sortHandler(sortKey) : null}>
      <Typography align={align}>
        {label}
        {sortBy === sortKey &&
          (orderBy === OrderType.ASCENDING ? (
            <ArrowIcon className={classes.arrowUp} />
          ) : orderBy === OrderType.DESCENDING ? (
            <ArrowIcon className={classes.arrowDown} />
          ) : null)}
      </Typography>
    </th>
  );

  const openDialogHandler = index => () => {
    setSelectedRowIndex(index);
    setIsOpenDialog(true);
    setStatus(STATUS.ACCEPT_BEFORE);
  };

  const closeDialogHandler = () => {
    setIsOpenDialog(false);
  };

  const onSuccessAcceptOffer = () => {
    setStatus(STATUS.ACCEPT_SUCCESS);
    fetchAsset(); // TODO: check later
  };

  const onErrorAcceptOffer = () => {
    setStatus(STATUS.ACCEPT_ERROR);
  };

  const onConfirmAcceptHandler = receit => {
    console.log('onConfirmAcceptHandler ==>', receit);
  };

  const onBlockConfirm = confirmationNumber => {
    console.log('onBlockConfirm confirmationNumber ==>', confirmationNumber);
  };

  const acceptOfferHandler = async () => {
    setStatus(STATUS.ACCEPT_WAITING);
    try {
      await acceptOffer({
        _nftCollateralId: offers[selectedRowIndex].nftCollateralId,
        _lender: offers[selectedRowIndex].lender,
        _onSuccess: onSuccessAcceptOffer,
        _onConfirm: onConfirmAcceptHandler,
        _onError: onErrorAcceptOffer,
        _onReceipt: onConfirmAcceptHandler,
        _onBlockConfirmed: onBlockConfirm,
      });
    } catch (error) {
      console.log('error ==>', error);
    }
  };

  const changeRowHandler = index => () => {
    setSelectedRowIndex(index);
  };

  const removeOfferHandler = index => async () => {
    const currentOffer = offers[index];
    try {
      const result = await deleteOffer({
        _nftCollateralId: currentOffer.nftCollateralId,
        _lender: currentOffer.lender,
      });

      setOffers(
        offers.filter(
          offer =>
            !(
              offer.nftCollateralId === currentOffer.nftCollateralId &&
              offer.lender === currentOffer.lender
            ),
        ),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const sortedOffers =
    orderBy === OrderType.DEFAULT
      ? offers
      : offers?.length
      ? offers
          .map((offer, index) => ({
            ...offer,
            index: index,
            tokenId: parseInt(offer.nftCollateralId),
            rate: calculateRate(offer.loanPrincipalAmount, offer.maximumRepaymentAmount),
          }))
          .sort(compareByValue(sortBy, orderBy === OrderType.ASCENDING))
      : [];

  return (
    <SyncCard>
      <Typography variant='h5' gutterBottom>
        {title}
      </Typography>
      <table className={classes.table}>
        <thead>
          <tr>
            {!hideAssetName && <TH label='Asset' sortKey='tokenId' align='left' />}
            <TH label='OFFERED BY' align='left' />
            <TH label='DURATION' sortKey='loanDuration' align='center' />
            <TH label='LOAN VALUE' sortKey='loanPrincipalAmount' align='right' />
            <TH label='REPAYMENT' sortKey='maximumRepaymentAmount' align='right' />
            <TH label='Rate %' sortKey='rate' align='right' />
          </tr>
        </thead>
        <tbody>
          {(sortedOffers || []).map((offer, index) => (
            <tr
              key={index}
              className={clsx(classes.tableRow, index === selectedRowIndex && classes.active)}
              onClick={changeRowHandler(index)}>
              {!hideAssetName && (
                <td>
                  <Typography className={classes.assetName} align='left' noWrap>
                    <Link href={`${PAGES.ASSETS.url}/${offer.nftCollateralId}`}>
                      <a>#{offer.nftCollateralId}</a>
                    </Link>
                  </Typography>
                </td>
              )}
              <td>
                <Typography align='left'>
                  <Link href={`${PAGES.ACCOUNT.url}/${offer.lender}`}>
                    <a className={classes.link}>
                      {offer?.lender === address ? 'You' : offer?.lender?.slice(0, 6)}
                    </a>
                  </Link>
                </Typography>
              </td>
              <td>
                <Typography>{getDays(offer.loanDuration)} days</Typography>
              </td>
              <td>
                <Typography align='right'>
                  {offer.loanPrincipalAmount / 1e18} {cryptoMap[offer.loanERC20Denomination]}
                </Typography>
              </td>
              <td>
                <Typography align='right'>
                  {offer.maximumRepaymentAmount / 1e18} {cryptoMap[offer.loanERC20Denomination]}
                </Typography>
              </td>
              <td>
                <Box display='flex' alignItems='center' gap='small' justifyContent='flex-end'>
                  <Typography align='right'>
                    {calculateRate(
                      offer.loanPrincipalAmount,
                      offer.maximumRepaymentAmount,
                      getDays(offer.loanDuration),
                    )}
                    %
                  </Typography>
                </Box>
              </td>
              <td>
                {isOwner && assetStatus === 'listed' ? (
                  <ContainedButton onClick={openDialogHandler(index)}>Accept</ContainedButton>
                ) : null}
                {[offer.lender.toLowerCase()].includes(address.toLowerCase()) && (
                  <ContainedButton onClick={removeOfferHandler(index)}>Remove</ContainedButton>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isOpenDialog && (
        <AcceptOfferDialog
          open={isOpenDialog}
          status={status}
          setOpen={setIsOpenDialog}
          onClose={closeDialogHandler}
          acceptHandler={acceptOfferHandler}
        />
      )}
    </SyncCard>
  );
}

export default OffersTable;
